import { PricingPlan } from "@/primary-models";
import { TraySize } from "@/constants/products";

import {
  allMealPlansByTraySize,
  ALL_MEAL_PLANS_TRAY_SIZE_A,
  freezerMealPlansByTraySize,
  saverMealPlansByTraySize,
  halfMealPlansByTraySize,
} from "./mealPlanSuggestions";
import { CancellationReasonForMealPlan } from "./cancellation-reason";

export const getCurrentMealPlanOptions = (currentMealPlan: PricingPlan, currentTraySize: TraySize) => {
  // if the tray size is not provided, we default to tray size A
  const allMealPlans = allMealPlansByTraySize[currentTraySize ?? TraySize.A];

  // First check if it's in the allMealPlans array
  const planFromArray = allMealPlans.find((mealPlan) => mealPlan.value === currentMealPlan);

  if (planFromArray) {
    return planFromArray;
  }

  // If not found in allMealPlans, check individual plan objects
  // This handles cases like freezer meal plans which are not in allMealPlans
  if (currentMealPlan === PricingPlan.FREEZER) {
    return freezerMealPlansByTraySize[currentTraySize];
  }

  return undefined;
};

export function getMealPlanSuggestions({
  cancellationReason,
  currentMealPlan,
  currentTraySize,
  selectedMealPlan,
}:{
  cancellationReason?: CancellationReasonForMealPlan;
  currentMealPlan?: PricingPlan;
  currentTraySize?: TraySize;
  selectedMealPlan?: PricingPlan;
}) {
  // if any of the required parameters are not provided, we fallback to suggesting all meal plans for tray size A
  if (!currentMealPlan || !cancellationReason) {
    return ALL_MEAL_PLANS_TRAY_SIZE_A
      .map((mealPlan) => ({
        ...mealPlan,
        advanced_options: { ...mealPlan.advanced_options, current: mealPlan.value === currentMealPlan },
        selected: selectedMealPlan === mealPlan.value,
      }));
  }

  // if the tray size is not provided, we default to tray size A
  const allMealPlans = allMealPlansByTraySize[currentTraySize ?? TraySize.A];
  const currentMealPlanOptions = getCurrentMealPlanOptions(currentMealPlan, currentTraySize ?? TraySize.A);

  // if the current meal plan is not found, we fallback to suggesting all meal plans
  if (!currentMealPlanOptions) {
    return allMealPlans
      .map((mealPlan) => ({
        ...mealPlan,
        advanced_options: { ...mealPlan.advanced_options, current: mealPlan.value === currentMealPlan },
        selected: selectedMealPlan === mealPlan.value,
      }));
  }

  // If the user is cancelling because the meal plan is too expensive and they are not on the saver plan,
  // we suggest their current meal plan and the saver plan
  if (
    cancellationReason === CancellationReasonForMealPlan.TOO_EXPENSIVE_OVER_MY_BUDGET &&
    currentMealPlan !== PricingPlan.SAVER
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...saverMealPlansByTraySize[currentTraySize ?? TraySize.A],
        selected: selectedMealPlan === PricingPlan.SAVER,
      },
    ];
  }

  // If the user is cancelling because the meal plan is too expensive and they are on the saver plan,
  // we suggest the half meal plan and their current meal plan
  if (
    cancellationReason === CancellationReasonForMealPlan.TOO_EXPENSIVE_OVER_MY_BUDGET &&
    currentMealPlan === PricingPlan.SAVER
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...halfMealPlansByTraySize[currentTraySize ?? TraySize.A],
        selected: selectedMealPlan === PricingPlan.HALF,
      },
    ];
  }

  // If the user is cancelling because the meal plan is too expensive compared to other brands
  // and they are on the standard or freezer plan, we suggest their current meal plan and the half meal plan
  if (
    cancellationReason === CancellationReasonForMealPlan.TOO_EXPENSIVE_OTHER_BRANDS_ARE_CHEAPER &&
      (currentMealPlan === PricingPlan.STANDARD || currentMealPlan === PricingPlan.FREEZER)
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...saverMealPlansByTraySize[currentTraySize ?? TraySize.A],
        selected: selectedMealPlan === PricingPlan.SAVER,
      },
      {
        ...halfMealPlansByTraySize[currentTraySize ?? TraySize.A],
        selected: selectedMealPlan === PricingPlan.HALF,
      },
    ];
  }

  // If the user is cancelling because the meal plan is too expensive compared to other brands
  // and they are on any other meal plan, we suggest all meal plans
  if (cancellationReason === CancellationReasonForMealPlan.TOO_EXPENSIVE_OTHER_BRANDS_ARE_CHEAPER) {
    return allMealPlans.map((mealPlan) => ({
      ...mealPlan,
      selected: selectedMealPlan === mealPlan.value,
      advanced_options: {
        ...mealPlan.advanced_options,
        current: mealPlan.value === currentMealPlan,
      },
    }));
  }

  // If the user is cancelling because they think it's not worth the cost
  // and they are on the standard or freezer plan,
  // we suggest their current meal plan, the half meal plan, and the saver plan
  if (
    cancellationReason === CancellationReasonForMealPlan.TOO_EXPENSIVE_NOT_WORTH_THE_COST &&
    (currentMealPlan === PricingPlan.STANDARD || currentMealPlan === PricingPlan.FREEZER)
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...saverMealPlansByTraySize[currentTraySize ?? TraySize.A],
        selected: selectedMealPlan === PricingPlan.SAVER,
      },
      {
        ...halfMealPlansByTraySize[currentTraySize ?? TraySize.A],
        selected: selectedMealPlan === PricingPlan.HALF,
      },
    ];
  }

  // If the user is cancelling because they think it's not worth the cost
  // and they are on any other meal plan, we suggest all meal plans
  if (cancellationReason === CancellationReasonForMealPlan.TOO_EXPENSIVE_NOT_WORTH_THE_COST) {
    return allMealPlans.map((mealPlan) => ({
      ...mealPlan,
      selected: selectedMealPlan === mealPlan.value,
      advanced_options: {
        ...mealPlan.advanced_options,
        current: mealPlan.value === currentMealPlan,
      },
    }));
  }

  // If the user is cancelling because they have accumulated too much meal in their freezer
  // and they are on any plan except the freezer plan, we suggest their current meal plan and the freezer plan
  if (
    cancellationReason === CancellationReasonForMealPlan.FREEZER_SPACE_ACCUMULATION &&
    currentMealPlan !== PricingPlan.FREEZER
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...freezerMealPlansByTraySize[currentTraySize ?? TraySize.A],
        selected: selectedMealPlan === PricingPlan.FREEZER,
      },
    ];
  }

  // If the user is cancelling because they have accumulated too much meal in their freezer
  // and they are on the freezer plan, we suggest all meal plans except the freezer plan
  if (cancellationReason === CancellationReasonForMealPlan.FREEZER_SPACE_ACCUMULATION) {
    return allMealPlans
      .map((mealPlan) => ({
        ...mealPlan,
        selected: selectedMealPlan === mealPlan.value,
        advanced_options: {
          ...mealPlan.advanced_options,
          current: mealPlan.value === currentMealPlan,
        },
      }));
  }

  // if the user is cancelling because they don't have enough space in their freezer
  // and they are on any plan except the freezer plan, we suggest their current meal plan and the freezer plan
  if (
    cancellationReason === CancellationReasonForMealPlan.FREEZER_SPACE_NOT_ENOUGH_SPACE &&
    currentMealPlan !== PricingPlan.FREEZER
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...freezerMealPlansByTraySize[currentTraySize ?? TraySize.A],
        selected: selectedMealPlan === PricingPlan.FREEZER,
      },
    ];
  }

  // If the user is cancelling because they don't have enough space in their freezer
  // and they are on the freezer plan, we suggest all meal plans except the freezer plan
  if (cancellationReason === CancellationReasonForMealPlan.FREEZER_SPACE_NOT_ENOUGH_SPACE) {
    return allMealPlans
      .map((mealPlan) => ({
        ...mealPlan,
        selected: selectedMealPlan === mealPlan.value,
        advanced_options: {
          ...mealPlan.advanced_options,
          current: mealPlan.value === currentMealPlan,
        },
      }));
  }

  // If the user is cancelling because they think the subscription doesn't work for them and they need less food
  // and they are on any other meal plan except the half meal plan,
  // we suggest their current meal plan and the half meal plan
  if (
    cancellationReason === CancellationReasonForMealPlan.SUBSCRIPTION_DOESNT_WORK_NEED_LESS_FOOD &&
    currentMealPlan !== PricingPlan.HALF
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...halfMealPlansByTraySize[currentTraySize ?? TraySize.A],
        selected: selectedMealPlan === PricingPlan.HALF,
      },
    ];
  }

  // If the user is cancelling because they think the subscription doesn't work for them and they need less food
  // and they are on the half meal plan, we suggest all meal plans except the half meal plan
  if (cancellationReason === CancellationReasonForMealPlan.SUBSCRIPTION_DOESNT_WORK_NEED_LESS_FOOD) {
    return allMealPlans
      .filter((mealPlan) => mealPlan.value !== PricingPlan.HALF)
      .map((mealPlan) => ({
        ...mealPlan,
        selected: selectedMealPlan === mealPlan.value,
        advanced_options: {
          ...mealPlan.advanced_options,
          current: mealPlan.value === currentMealPlan,
        },
      }));
  }

  // if the user is cancelling but they plan to to come back as
  // they are currently feeding their cat with other foods alongside Katkin
  // and they are on any plan except the half meal plan, we suggest their current meal plan and the half meal plan
  if (
    cancellationReason === CancellationReasonForMealPlan.COMING_BACK_FEEDING_OTHER_FOODS_ALONGSIDE_KATKIN &&
    currentMealPlan !== PricingPlan.HALF
  ) {
    return [
      {
        ...currentMealPlanOptions,
        selected: selectedMealPlan === currentMealPlanOptions.value,
        advanced_options: {
          ...currentMealPlanOptions.advanced_options,
          current: currentMealPlanOptions.value === currentMealPlan,
        },
      },
      {
        ...halfMealPlansByTraySize[currentTraySize ?? TraySize.A],
        selected: selectedMealPlan === PricingPlan.HALF,
      },
    ];
  }

  // if the user is cancelling but they plan to come back as
  // they are currently feeding their cat with other foods alongside Katkin
  // and they are on the half meal plan, we suggest all meal plans except the half meal plan
  if (cancellationReason === CancellationReasonForMealPlan.COMING_BACK_FEEDING_OTHER_FOODS_ALONGSIDE_KATKIN) {
    return allMealPlans
      .filter((mealPlan) => mealPlan.value !== PricingPlan.HALF)
      .map((mealPlan) => ({
        ...mealPlan,
        selected: selectedMealPlan === mealPlan.value,
        advanced_options: {
          ...mealPlan.advanced_options,
          current: mealPlan.value === currentMealPlan,
        },
      }));
  }

  // if the user is cancelling for any other reason, we suggest all meal plans
  return allMealPlans.map((mealPlan) => ({
    ...mealPlan,
    selected: selectedMealPlan === mealPlan.value,
    advanced_options: {
      ...mealPlan.advanced_options,
      current: mealPlan.value === currentMealPlan,
    },
  }));
}
